// Where the ink of a line of text starts: the highest point its letters reach (ascenders and
// accents, a text shadow), and the tops of pictures and painted inline boxes on it.
//
// A text fragment's rectangle is its font's ascent and descent, not its glyphs: tall letters and
// accents can reach its top (or past it), short ones start well below it. So the glyphs are
// measured with canvas text metrics, in the same font, on a canvas that is never added to the page.
// The whole text of a node is measured, uppercased too (text-transform), so the result is never
// lower than what is drawn.

const SKIP = new Set(['script', 'style', 'noscript', 'template', 'head', 'link', 'meta', 'title']);
const REPLACED = new Set(['img', 'svg', 'video', 'canvas', 'iframe', 'object', 'embed', 'picture', 'input', 'button', 'select', 'textarea', 'meter', 'progress']);
const LIMIT = 400;            // nodes looked at per line (a nav bar with many links)
const LONGEST = 300;          // characters of a text node measured

export function createInk(doc) {
  const win = doc.defaultView;
  let ctx = null, broken = false;
  const offsets = new Map();  // font and text -> how far below its fragment's top the ink starts

  // How far below the top of a text fragment its ink starts (negative: above it).
  function offsetOf(cs, text) {
    const font = `${cs.fontStyle} ${cs.fontVariantCaps === 'small-caps' ? 'small-caps ' : ''}${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
    text = text.length > LONGEST ? text.slice(0, LONGEST) : text;
    const key = font + '\n' + text;
    let offset = offsets.get(key);
    if (offset === undefined) {
      offset = glyphs(font, text, parseFloat(cs.fontSize) || 16);
      if (offsets.size > 2000) offsets.clear();
      offsets.set(key, offset);
    }
    return offset - decoration(cs);
  }
  function glyphs(font, text, size) {
    if (!ctx && !broken) {
      ctx = doc.createElement('canvas').getContext('2d');
      broken = !ctx || typeof ctx.measureText !== 'function';
    }
    if (broken) return -0.1 * size;                          // no canvas: a little above the fragment
    ctx.font = '1px __companions_unset__';
    const unset = ctx.font;
    ctx.font = font;
    if (ctx.font === unset) return -0.1 * size;                // a font the canvas can't parse
    const m = ctx.measureText(text + ' ' + text.toUpperCase());
    if (!Number.isFinite(m.fontBoundingBoxAscent) || !Number.isFinite(m.actualBoundingBoxAscent)) return -0.1 * size;
    return m.fontBoundingBoxAscent - m.actualBoundingBoxAscent;
  }
  // A text shadow or stroke reaches above the glyphs; an overline sits at the fragment's top.
  function decoration(cs) {
    let up = 0;
    const stroke = parseFloat(cs.webkitTextStrokeWidth) || 0;
    if (stroke > 0) up = stroke / 2;
    if (cs.textShadow && cs.textShadow !== 'none') {
      for (const shadow of cs.textShadow.split(/,(?![^(]*\))/)) {
        const [, y = 0, blur = 0] = (shadow.replace(/rgba?\([^)]*\)|#[0-9a-f]+|[a-z]+\([^)]*\)/gi, '').match(/-?[\d.]+px/g) || []).map(parseFloat);
        up = Math.max(up, blur - y);
      }
    }
    if (/overline/.test(cs.textDecorationLine || '')) up = Math.max(up, (parseFloat(cs.fontSize) || 16) * 0.1);
    return up;
  }
  const paints = cs => cs.backgroundImage !== 'none' || cs.boxShadow !== 'none' || alpha(cs.backgroundColor) > 0
    || ['Top', 'Right', 'Bottom', 'Left'].some(side => parseFloat(cs[`border${side}Width`]) > 0 && !/none|hidden/.test(cs[`border${side}Style`]));

  // The top of the ink on the line between line.t and line.b (client pixels), from the nodes given
  // ([first, last] sibling runs, or [null, null] for all of `el`), or null if nothing is drawn there.
  function lineTop(el, runs, line) {
    let best = Infinity, seen = 0;
    const range = doc.createRange();
    const onLine = q => q.width > 0 && q.height > 0 && (q.top + q.bottom) / 2 >= line.t - 0.5 && (q.top + q.bottom) / 2 <= line.b + 0.5;
    const across = q => q.width > 0 && q.height > 0 && q.top < line.b && q.bottom > line.t;
    function visit(node) {
      if (++seen > LIMIT) return;
      if (node.nodeType === 3) {
        if (!/\S/.test(node.data) || !node.parentElement) return;
        const cs = win.getComputedStyle(node.parentElement);
        if (cs.visibility !== 'visible') return;
        range.selectNodeContents(node);
        let top = Infinity;
        for (const q of range.getClientRects()) if (onLine(q)) top = Math.min(top, q.top);
        if (top < Infinity) best = Math.min(best, top + offsetOf(cs, node.data.trim()));
        return;
      }
      if (node.nodeType !== 1 || SKIP.has(node.localName)) return;
      const cs = win.getComputedStyle(node);
      if (cs.display === 'none' || parseFloat(cs.opacity) < 0.05) return;
      const replaced = REPLACED.has(node.localName);
      if (cs.visibility === 'visible' && (replaced || paints(cs))) {
        for (const q of node.getClientRects()) if (across(q)) best = Math.min(best, q.top);
      }
      if (replaced) return;
      for (const child of (node.shadowRoot || node).childNodes) visit(child);
    }
    for (const [first, last] of runs) {
      if (!first) { for (const child of el.childNodes) visit(child); continue; }
      if (!first.isConnected || !last.isConnected) continue;
      for (let n = first; n; n = n.nextSibling) { visit(n); if (n === last) break; }
    }
    return best < Infinity ? best : null;
  }

  return { lineTop, clear: () => offsets.clear() };
}

function alpha(color) {
  if (!color || color === 'transparent') return 0;
  const m = /rgba?\(([^)]*)\)/.exec(color);
  if (!m) return 1;                                          // a color space we don't parse: treated as painted
  const parts = m[1].split(/[\s,/]+/).filter(Boolean);
  if (parts.length < 4) return 1;
  const a = parts[3];
  return a.endsWith('%') ? parseFloat(a) / 100 : parseFloat(a);
}
